import React from 'react'
import { Button, Card, Columns, Element, Heading, Icon } from 'react-bulma-components'
import { WorkersRequest } from '../../api/workers-requests'
import AssignWorkerForm from './forms/assign-worker-form/assign-worker-form'
import { Mission } from '../../api/missions'
import { Link } from 'react-router-dom'
import { MissionAbandonedMessage, MissionIsCancelledMessage } from '../missions/mission-messages'
import WorkersRequestPostMessages from './posts/workers-requests-posts-messages'
import useStore from '../../store'
import {
  WorkersRequestRecourseCaseJustification,
  WorkersRequestRecourseCaseReason,
} from './recourse-case'
import Avatar from '../avatar/avatar'
import PostSuggestions from './post-suggestions'
import { useUpdatePost } from '../../queries/workers-requests'
import { getUserLabel, getWorkerLaborCostCoefficientKind } from '../../utils/users'
import WorkerLaborCostCoefficientKindLabel from '../users/worker-labor-cost-coefficient-kind-label'
import { ConfirmButton } from '../buttons/confirm-button'
import { WorkersRequestPostsSuggestions } from '../../hooks/use-workers-request-posts-suggestions'
import WorkPeriodTags from '../work-periods/work-period-tags'

interface WorkersRequestPostListItemProps {
  post: WorkersRequest['posts'][0]
  workersRequest: WorkersRequest
  count?: {
    current: number
    total: number
  }
  suggestions: WorkersRequestPostsSuggestions
}

const WorkersRequestPostListItem: React.FC<WorkersRequestPostListItemProps> = ({
  post,
  workersRequest,
  count,
  suggestions,
}) => {
  const currentUserRole = useStore(state => state.session.currentUserRole)

  const postUpdateMutation = useUpdatePost()

  const workersWithMissionCorrespondingToPost = suggestions.workers.filter(worker =>
    worker.missionCorrespondingJobTitleIds.includes(post.jobTitle._id),
  )

  const activeMission = post?.missions.find(
    mission => !mission?.isCancelled && !mission?.isAbandoned, // TODO: better check. Last item in list if post is not replaceable?
  )
  const showPostCloseOpenAction =
    (currentUserRole === 'interimAgencyMember' && post.isReplaceable === true) ||
    (currentUserRole === 'employerMember' && post.isReplaceable === false)
  const postMissions: (Mission | undefined)[] = [...(post?.missions || [])]
  if (!activeMission) postMissions.push(undefined)

  const baseHourlyCost = activeMission?.costs.baseHourlyCost
  const officialHourlyCost = activeMission?.costs.officialHourlyCost

  return (
    <Card mb={5}>
      <Card.Content pb={3}>
        <Element>
          <Element display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Heading size={5} mb={1}>
              <Element renderAs="span">
                #{count?.current} {post.jobTitle.name}
              </Element>
              <Element renderAs="span" ml={2}>
                <WorkPeriodTags
                  tags={post.tags || []}
                  isEditable={false}
                  hideEmptyMessage
                  size="small"
                  formProps={{
                    onSubmit: () => {},
                    onCancel: () => {},
                    dateRange: false,
                  }}
                />
              </Element>
            </Heading>
            <WorkersRequestPostMessages post={post} type="tag" />
          </Element>
          <Columns textSize={7}>
            <Columns.Column size={6} py={2}>
              <Element mb={1} textWeight="bold">
                Description de la Demande
              </Element>
              {post.description || 'N/A'}
            </Columns.Column>
            <Columns.Column size={6} py={2}>
              <Element mb={1} textWeight="bold">
                Informations complémentaires pour l'Intérimaire
              </Element>
              <ul>
                {post.jobTitle.complementaryInformations ? (
                  <li>{post.jobTitle.complementaryInformations}</li>
                ) : undefined}
                {workersRequest.missionData.complementaryInformations ? (
                  <li>{workersRequest.missionData.complementaryInformations}</li>
                ) : undefined}
                {!post.jobTitle.complementaryInformations &&
                  !workersRequest.missionData.complementaryInformations && <li>N/A</li>}
              </ul>
            </Columns.Column>
            {activeMission && activeMission.costs.interimAgencyCostCoefficient && (
              <Columns.Column size={6} py={2}>
                <Element mb={1} textWeight="bold">
                  Coût horaire de base
                </Element>
                {baseHourlyCost ? (
                  <Element renderAs="span">{baseHourlyCost} €/h</Element>
                ) : (
                  <Element renderAs="span">
                    {officialHourlyCost} €/h<Element renderAs="small"> (SMIC)</Element>
                  </Element>
                )}
              </Columns.Column>
            )}
            <Columns.Column size={6} py={2}>
              <Element mb={1} textWeight="bold">
                Cas de recours et justification
              </Element>
              <WorkersRequestRecourseCaseReason recourseCase={post.recourseCase} /> (
              <WorkersRequestRecourseCaseJustification recourseCase={post.recourseCase} />)
            </Columns.Column>
          </Columns>
        </Element>
        <br />
        {postMissions.map((mission, index) => {
          return (
            <Element
              mb={index === postMissions.length - 1 ? undefined : 3}
              key={mission?._id || index}
            >
              <PostMission
                key={mission?._id || 'new'}
                post={post}
                mission={mission}
                workersRequest={workersRequest}
              />
            </Element>
          )
        })}
        {!activeMission && showPostCloseOpenAction && (
          <ConfirmButton>
            <Button
              mt={4}
              data-test="action-switch-isReplaceable"
              color={'primary'}
              outlined
              size={'small'}
              fullwidth
              onClick={() => {
                postUpdateMutation.mutate({
                  postId: post._id,
                  workersRequestId: workersRequest._id,
                  isReplaceable: !post.isReplaceable,
                })
              }}
            >
              {post.isReplaceable
                ? 'Pas de candidat pour ce poste'
                : 'Réouvrir le poste et demander un candidat'}
            </Button>
          </ConfirmButton>
        )}
        <br />
        {!activeMission && (
          <PostSuggestions
            post={post}
            workersRequest={workersRequest}
            availableWorkers={
              currentUserRole === 'interimAgencyMember' ? suggestions.workers || [] : undefined
            }
            workersWithMissionCorrespondingToPost={
              currentUserRole === 'interimAgencyMember'
                ? workersWithMissionCorrespondingToPost
                : undefined
            }
          />
        )}
      </Card.Content>
    </Card>
  )
}

export default WorkersRequestPostListItem

interface WorkersRequestPostListMissionProps
  extends Omit<WorkersRequestPostListItemProps, 'suggestions'> {
  mission?: Mission
}

const PostMission: React.FC<WorkersRequestPostListMissionProps> = ({
  mission,
  workersRequest,
  post,
}) => {
  const laborCostCoefficientKind =
    (mission?.user &&
      getWorkerLaborCostCoefficientKind(
        mission.user,
        workersRequest.interimAgency,
        workersRequest.employer,
      )) ||
    'delegation'
  return (
    <Element key={mission?._id || 'new'}>
      {mission?.user && (
        <Element mr={2} renderAs="span">
          <Avatar user={mission.user} small />
        </Element>
      )}
      {mission ? (
        <>
          <PostMissionLink mission={mission} />
          <Element renderAs="small">
            {' '}
            /{' '}
            <WorkerLaborCostCoefficientKindLabel
              laborCostCoefficientKind={laborCostCoefficientKind}
            />
          </Element>
        </>
      ) : (
        post.isReplaceable && (
          <AssignWorkerForm workersRequest={workersRequest} postId={post?._id} />
        )
      )}
    </Element>
  )
}

const PostMissionLink: React.FC<{ mission: Mission }> = ({ mission }) => {
  return (
    <Element renderAs="span">
      <Link to={`/missions/${mission._id}`}>
        <Icon.Text>
          {mission.isCancelled && <MissionIsCancelledMessage mission={mission} type="icon" />}
          {mission.isAbandoned && <MissionAbandonedMessage mission={mission} type="icon" />}
          <span
            style={
              mission.isCancelled || mission.isAbandoned ? { textDecoration: 'line-through' } : {}
            }
          >
            {getUserLabel(mission.user)}
          </span>
        </Icon.Text>
      </Link>
    </Element>
  )
}
